import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import Formulario from "../../Componentes/Utils/Formulario";
import FormComp from "../../Componentes/FormComp";
import useApi from "../../hooks/useApi";
import useValidateColaborador from "../../hooks/useValidateColaborador";

const TIPOS_LICENCA_ENDPOINT = "/Painel/API/InventarioTI/GetTiposLicenca/";
const SAVE_COLAB_LICENCA_ENDPOINT = "/Painel/API/InventarioTI/SaveColabLicenca/";
const VERIFICAR_LICENCA_ATIVA_ENDPOINT =
  "/Painel/API/InventarioTI/VerificarColabLicencaAtiva/";

const TIPO_OPTIONS = [
  { value: "COLAB", label: "COLABORADOR" },
  { value: "TERCEIRO", label: "TERCEIRO" },
];

const SectionTitle = styled.p`
  margin: 4px 0 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #263b4b;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 38px;
  border-radius: 8px;
  border: 1px solid #94a3b8;
  padding: 0 12px;
  box-sizing: border-box;
  outline: none;
  font-size: 13px;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 1px #2563eb;
  }
`;

const ListShell = styled.div`
  margin-top: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  max-height: 260px;
  overflow-y: auto;
  background: #fff;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #c1c1c1;
    border-radius: 4px;
  }
`;

const ListHeader = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 12px;
  color: #475569;
  z-index: 1;
`;

const LicencaRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #eef2f7;
  cursor: pointer;
  font-size: 13px;
  color: #263b4b;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f1f5f9;
  }
`;

const LicencaLabel = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyRow = styled.div`
  padding: 18px 12px;
  text-align: center;
  font-size: 13px;
  color: #64748b;
`;

const Counter = styled.span`
  margin-left: auto;
  font-weight: 600;
  color: #263b4b;
`;

const formatLicencaLabel = (item) =>
  `${String(item?.fornecedor ?? "").trim()} - ${String(item?.tipo ?? "").trim()}`;

export default function ModalLancarLicencaColaborador({ onClose, onSaved }) {
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const [tipo, setTipo] = useState("COLAB");
  const [matricula, setMatricula] = useState("");
  const [nomeExibicao, setNomeExibicao] = useState("");
  const [email, setEmail] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [busca, setBusca] = useState("");
  const [tiposLicenca, setTiposLicenca] = useState([]);
  const [loadingTipos, setLoadingTipos] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [verificandoLicencaAtiva, setVerificandoLicencaAtiva] = useState(false);
  const [licencaAtivaError, setLicencaAtivaError] = useState("");

  const isColab = tipo === "COLAB";
  const isTerceiro = tipo === "TERCEIRO";

  const matriculaParaValidar = isColab ? matricula : "";

  const {
    info: colabInfo,
    validating,
    error: validationError,
  } = useValidateColaborador(matriculaParaValidar, { bloquearDemitido: true });

  const colaboradorValidoProtheus =
    isColab && Boolean(colabInfo) && !validating && !validationError;

  const nomeTerceiroValido = useMemo(() => {
    return String(nomeExibicao || "").trim().length > 0;
  }, [nomeExibicao]);

  const emailValido = useMemo(() => {
    const e = String(email || "").trim();
    if (!e) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  }, [email]);

  const pessoaPronta =
    (isColab && colaboradorValidoProtheus) ||
    (isTerceiro && nomeTerceiroValido && emailValido);

  const pessoaValida =
    pessoaPronta && !verificandoLicencaAtiva && !licencaAtivaError;

  const canSubmit = useMemo(() => {
    if (saving || loadingTipos || selecionados.size === 0) return false;
    if (!emailValido) return false;
    if (!pessoaValida) return false;
    if (isColab) return matricula.trim().length === 6;
    return nomeTerceiroValido;
  }, [
    emailValido,
    isColab,
    loadingTipos,
    matricula,
    nomeTerceiroValido,
    pessoaValida,
    saving,
    selecionados.size,
  ]);

  useEffect(() => {
    setLicencaAtivaError("");
    setVerificandoLicencaAtiva(false);
    setSelecionados(new Set());
    setBusca("");
    if (isColab) {
      setNomeExibicao("");
    } else {
      setMatricula("");
    }
  }, [isColab, tipo]);

  useEffect(() => {
    let cancelled = false;

    const verificarLicencasAtivas = async () => {
      if (isColab) {
        const mat = String(matricula || "").trim();
        if (mat.length !== 6 || !colaboradorValidoProtheus) {
          setLicencaAtivaError("");
          setVerificandoLicencaAtiva(false);
          return;
        }

        setVerificandoLicencaAtiva(true);
        setLicencaAtivaError("");

        try {
          const { data } = await apiRef.current.get(
            `${VERIFICAR_LICENCA_ATIVA_ENDPOINT}?tipo=COLAB&matricula=${mat}`
          );

          if (cancelled) return;

          if (data?.possui_licenca_ativa) {
            setLicencaAtivaError(
              "Este colaborador já possui licenças ativas. Abra o cadastro na tabela para adicionar novas licenças."
            );
          }
        } catch (err) {
          if (cancelled) return;
          console.error(err);
          setLicencaAtivaError(
            err?.response?.data?.message ||
              "Erro ao verificar licenças ativas do colaborador."
          );
        } finally {
          if (!cancelled) {
            setVerificandoLicencaAtiva(false);
          }
        }
        return;
      }

      const emailNorm = String(email || "").trim().toLowerCase();
      if (!nomeTerceiroValido || !emailValido) {
        setLicencaAtivaError("");
        setVerificandoLicencaAtiva(false);
        return;
      }

      setVerificandoLicencaAtiva(true);
      setLicencaAtivaError("");

      try {
        const { data } = await apiRef.current.get(
          `${VERIFICAR_LICENCA_ATIVA_ENDPOINT}?tipo=TERCEIRO&email=${encodeURIComponent(
            emailNorm
          )}`
        );

        if (cancelled) return;

        if (data?.possui_licenca_ativa) {
          setLicencaAtivaError(
            "Esta pessoa terceira já possui licenças ativas. Abra o cadastro na tabela para adicionar novas licenças."
          );
        }
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        setLicencaAtivaError(
          err?.response?.data?.message ||
            "Erro ao verificar licenças ativas da pessoa terceira."
        );
      } finally {
        if (!cancelled) {
          setVerificandoLicencaAtiva(false);
        }
      }
    };

    verificarLicencasAtivas();

    return () => {
      cancelled = true;
    };
  }, [
    colaboradorValidoProtheus,
    email,
    emailValido,
    isColab,
    matricula,
    nomeTerceiroValido,
  ]);

  useEffect(() => {
    const carregarTipos = async () => {
      setLoadingTipos(true);
      try {
        const { data } = await apiRef.current.get(TIPOS_LICENCA_ENDPOINT);
        const list = Array.isArray(data) ? data : data?.data ?? [];
        setTiposLicenca(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error(err);
        alert(err?.response?.data?.message || "Erro ao carregar tipos de licença.");
      } finally {
        setLoadingTipos(false);
      }
    };

    carregarTipos();
  }, []);

  const filtrados = useMemo(() => {
    const query = busca.trim().toUpperCase();
    if (!query) return tiposLicenca;
    return tiposLicenca.filter((item) =>
      formatLicencaLabel(item).toUpperCase().includes(query)
    );
  }, [busca, tiposLicenca]);

  const todosFiltradosSelecionados =
    filtrados.length > 0 && filtrados.every((item) => selecionados.has(String(item.id)));

  const toggleLicenca = (id) => {
    const key = String(id);
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleTodos = () => {
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (todosFiltradosSelecionados) {
        filtrados.forEach((item) => next.delete(String(item.id)));
      } else {
        filtrados.forEach((item) => next.add(String(item.id)));
      }
      return next;
    });
  };

  const handleTipoChange = (e) => {
    const value = String(e?.target?.value || "COLAB").toUpperCase();
    setTipo(value === "TERCEIRO" ? "TERCEIRO" : "COLAB");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setSaving(true);

      const payload = {
        tipo,
        email: email.trim().toLowerCase(),
        observacoes: observacoes.trim() || null,
        ids_tipo_licenca: Array.from(selecionados).map((id) => Number(id)),
        lancamento_inicial: true,
      };

      if (isColab) {
        payload.matricula = matricula.trim();
      } else {
        payload.nome_exibicao = nomeExibicao.trim();
      }

      await apiRef.current.post(SAVE_COLAB_LICENCA_ENDPOINT, payload);

      onClose?.();
      onSaved?.();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao lançar licença(s).");
    } finally {
      setSaving(false);
    }
  };

  const mostrarCamposLicenca = pessoaValida;

  return (
    <Overlay state={true}>
      <Modal header close={onClose} size="md">
        <Formulario onSubmit={handleSubmit}>
          <FormComp
            title="TIPO"
            type="select"
            options={TIPO_OPTIONS}
            obj
            valor={tipo}
            name="tipo"
            changeFunc={handleTipoChange}
            required
          />

          {isColab && (
            <>
              <FormComp
                title="MATRÍCULA DO COLABORADOR"
                type="numberOnly"
                placeholder="EX: 012345"
                valor={matricula}
                name="matricula"
                changeFunc={(e) => setMatricula(e.target.value.replace(/\D/g, ""))}
                required
                maxLength={6}
              />
              <FormComp
                title="NOME"
                type="text"
                readOnly
                valor={colabInfo?.nome || ""}
                placeholder={validating ? "Validando matrícula..." : ""}
              />
              <FormComp
                title="FUNÇÃO"
                type="text"
                readOnly
                valor={colabInfo?.funcao || ""}
              />
              <FormComp
                title="CENTRO DE CUSTO"
                type="text"
                readOnly
                valor={colabInfo?.centro_custo_desc || colabInfo?.centro_custo || ""}
              />
              {validationError && (
                <p style={{ color: "#B91C1C", fontSize: "12px", marginTop: "-6px" }}>
                  {validationError}
                </p>
              )}
            </>
          )}

          {isTerceiro && (
            <FormComp
              title="NOME"
              type="text"
              placeholder="EX: JOÃO DA SILVA"
              valor={nomeExibicao}
              name="nome_exibicao"
              changeFunc={(e) => setNomeExibicao(e.target.value)}
              required
            />
          )}

          {verificandoLicencaAtiva && pessoaPronta && (
            <p style={{ color: "#475569", fontSize: "12px", marginTop: "-6px" }}>
              Verificando licenças ativas...
            </p>
          )}
          {licencaAtivaError && (
            <p style={{ color: "#B91C1C", fontSize: "12px", marginTop: "-6px" }}>
              {licencaAtivaError}
            </p>
          )}

          {(mostrarCamposLicenca || isTerceiro) && (
            <>
              <FormComp
                title={isTerceiro ? "E-MAIL" : "E-MAIL DO COLABORADOR"}
                type="email"
                placeholder={
                  isTerceiro ? "EX: pessoa@empresa.com" : "EX: colaborador@palmont.com.br"
                }
                valor={email}
                name="email"
                changeFunc={(e) => setEmail(e.target.value)}
                required
                invalid={email.length > 0 && !emailValido}
              />
              {(mostrarCamposLicenca || (isTerceiro && emailValido && nomeTerceiroValido)) && (
                <FormComp
                  title="OBSERVAÇÕES"
                  type="textarea"
                  placeholder="INFORME OBSERVAÇÕES SOBRE AS LICENÇAS (OPCIONAL)"
                  valor={observacoes}
                  name="observacoes"
                  changeFunc={(e) => setObservacoes(e.target.value)}
                />
              )}
            </>
          )}

          {mostrarCamposLicenca && (
            <div>
              <SectionTitle>TIPOS DE LICENÇA</SectionTitle>
              <SearchInput
                placeholder="Pesquisar por fornecedor ou tipo..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />

              <ListShell>
                <ListHeader>
                  <input
                    type="checkbox"
                    checked={todosFiltradosSelecionados}
                    onChange={toggleTodos}
                    disabled={filtrados.length === 0 || loadingTipos}
                    aria-label="Selecionar todos"
                  />
                  <span>Selecionar todos</span>
                  <Counter>
                    {selecionados.size} selecionada
                    {selecionados.size === 1 ? "" : "s"} • {tiposLicenca.length} tipo
                    {tiposLicenca.length === 1 ? "" : "s"}
                  </Counter>
                </ListHeader>

                {loadingTipos ? (
                  <EmptyRow>Carregando tipos de licença...</EmptyRow>
                ) : filtrados.length === 0 ? (
                  <EmptyRow>
                    {tiposLicenca.length === 0
                      ? "Nenhum tipo de licença cadastrado."
                      : "Nenhum tipo encontrado para a busca."}
                  </EmptyRow>
                ) : (
                  filtrados.map((item) => {
                    const label = formatLicencaLabel(item);
                    return (
                      <LicencaRow key={item.id} title={label}>
                        <input
                          type="checkbox"
                          checked={selecionados.has(String(item.id))}
                          onChange={() => toggleLicenca(item.id)}
                        />
                        <LicencaLabel>{label}</LicencaLabel>
                      </LicencaRow>
                    );
                  })
                )}
              </ListShell>
            </div>
          )}

          <FormComp
            type="submit"
            valor={
              saving
                ? "SALVANDO..."
                : (isColab && validating) || verificandoLicencaAtiva
                ? "VALIDANDO..."
                : "SALVAR"
            }
            disabled={
              saving ||
              (isColab && validating) ||
              verificandoLicencaAtiva ||
              !canSubmit
            }
          />
        </Formulario>
      </Modal>
    </Overlay>
  );
}
